import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useMemo, useRef, useState, useCallback } from 'react';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenPodium from 'common/components/RewindModal/Konvas/ShareableScreenPodium';
import Slides from 'common/components/RewindModal/Slides';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';
import Card from './component/Card';

type ScreenTopGlobalSeriesProps = {
  entities: any;
  onNext: () => void;
  isLastStep: boolean;
  screenName: ScreenNames;
  shareText: string;
};

const ScreenTopGlobalSeriesContent = ({
  entities,
  onNext,
  isLastStep,
  screenName,
  shareText,
  isDownloadReady
}: ScreenTopGlobalSeriesProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const topItems = useMemo(() => entities?.[0]?.entities ?? [], [entities]);

  const getItemsToDisplay = () => {
    switch (screenName) {
      case 'screen_top_global_series_2025_reveal_1':
        return [topItems[4], topItems[3]].filter(Boolean);
      case 'screen_top_global_series_2025_reveal_2':
        return [topItems[2], topItems[1]].filter(Boolean);
      case 'screen_top_global_series_2025_reveal_3':
        return [topItems[0]].filter(Boolean);
      default:
        return [];
    }
  };

  const currentItems = getItemsToDisplay();

  const podiumItems = useMemo(() => {
    const validItems = topItems
      .slice(0, 3)
      .filter((item: any) => item && item.id);
    return validItems.map((item: any, index: number) => ({
      id: item.id,
      position: index + 1,
      poster: item.poster
    }));
  }, [topItems]);

  const title = 'Top séries';

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = `mon-top-series-allocine-2025.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error generating image:', error);
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  const isReveal3 = screenName === 'screen_top_global_series_2025_reveal_3';

  return (
    <>
      <Slides>
        <div className={classNames(styles.contentLayout)}>
          <div
            className={classNames(
              styles.topCardsLayout,
              `top-cards-${currentItems.length}`
            )}
          >
            {currentItems
              .filter((item: any) => item && item.id)
              .map((item: any) => (
                <div
                  key={item.id}
                  className={classNames(
                    styles.topCardItem,
                    `card-${topItems.indexOf(item) + 1}`
                  )}
                >
                  <Card path={item.poster?.path} />
                  <div className={styles.topCardRank}>
                    <span className={styles.topCardRankNum}>
                      {topItems.indexOf(item) + 1}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <BottomActions
          showShare={isReveal3}
          onShareClick={isReveal3 ? handleDownload : undefined}
          isShareDisabled={isReveal3 && (!isDownloadReady || isDownloading)}
          shareText={isReveal3 && isDownloading ? 'Génération...' : shareText}
          showNext={!isLastStep}
          onNextClick={onNext}
        />
      </Slides>

      {isReveal3 && (
        <ShareableScreenPodium
          ref={stageRef}
          data={{ title }}
          items={podiumItems}
          isSeries={true}
        />
      )}
    </>
  );
};

const ScreenTopGlobalSeries = (props: ScreenTopGlobalSeriesProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenTopGlobalSeriesContent
        {...props}
        isDownloadReady={isDownloadReady}
      />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenTopGlobalSeries;
